import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { Role } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/toast';

export interface AppShellNavItem {
  to: string;
  label: string;
}

interface AppShellProps {
  role: Role;
  nav: AppShellNavItem[];
  children: ReactNode;
}

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  faculty: 'Faculty',
  parent: 'Parent',
  dancer: 'Dancer',
};

export function AppShell({ role, nav, children }: AppShellProps) {
  async function handleSignOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // Surface a recoverable failure so the user knows their session
        // may still be valid server-side. The client clears local state
        // regardless, but the server should be the source of truth.
        toast({
          title: 'Sign-out failed',
          description: 'Your session may still be active. Please try again or close this browser.',
          variant: 'error',
        });
        return;
      }
    } catch (err) {
      toast({
        title: 'Sign-out failed',
        description: err instanceof Error ? err.message : 'Unexpected error',
        variant: 'error',
      });
      return;
    }
    window.location.assign('/login');
  }

  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-[16rem_1fr]">
      <header
        aria-label="Primary navigation"
        className="border-b border-[var(--color-border)] bg-[var(--color-surface-elevated)] lg:flex lg:h-dvh lg:flex-col lg:border-r lg:border-b-0"
      >
        <div className="flex h-16 items-center justify-between px-5 lg:h-20 lg:px-6">
          <Link
            to="/"
            className="font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-xl)]"
          >
            Pirouette
          </Link>
          <span className="tracking-[var(--tracking-wide)] text-[var(--color-ink-subtle)] text-[var(--text-xs)] uppercase">
            {ROLE_LABELS[role]}
          </span>
        </div>
        <nav
          className="px-3 py-4 lg:flex-1 lg:overflow-y-auto lg:py-6"
          aria-label={`${ROLE_LABELS[role]} navigation`}
        >
          <ul className="flex flex-row gap-1 overflow-x-auto lg:flex-col">
            {nav.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="block rounded-[var(--radius-md)] px-3 py-2 whitespace-nowrap text-[var(--color-ink-muted)] text-[var(--text-sm)] transition-colors duration-[var(--duration-fast)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)] aria-[current=page]:bg-[var(--color-accent-muted)] aria-[current=page]:font-medium aria-[current=page]:text-[var(--color-ink)]"
                  activeOptions={{ exact: false }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="px-5 pt-2 pb-5 lg:border-t lg:border-[var(--color-border)] lg:p-5">
          <Button variant="ghost" size="sm" onClick={() => void handleSignOut()}>
            Sign out
          </Button>
        </div>
      </header>

      <main id="main" className="px-6 py-8 lg:px-10 lg:py-12">
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>
    </div>
  );
}
